import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import moment from "moment/moment";

/* ================= CONSTANT ================= */

// Cột theo ca học
const SHIFT_COLUMNS = [
    "Đi học",
    "Có phép",
    "Không phép",
    "Giờ đến",
    "Giờ về",
    "Đến muộn (phút)",
    "Về sớm (phút)",
    "Về muộn (phút)",
];
const SHIFT_COL_SPAN = SHIFT_COLUMNS.length;

// Cột điểm danh ăn
const EAT_COLUMNS = [
    "Ăn sáng",
    "Phụ sáng",
    "Ăn trưa",
    "Ăn chiều",
    "Phụ chiều",
    "Ăn tối",
];
const EAT_COL_SPAN = EAT_COLUMNS.length;

const COLORS = {
    YELLOW: "f8eb00",
    GREEN: "95cb4a",
    RED_TEXT: "FFFF0000",
    LIGHT_GRAY: "E0E0E0",
    BLUE: "4F81BD",
    // MÀU RIÊNG CHO ĂN
    EAT_HEADER: "9BC2E6",      // xanh nhạt (tiêu đề)
    EAT_SUB_HEADER: "BDD7EE",  // xanh rất nhạt (bữa ăn)
    EAT_CELL: "E2EFDA",        // xanh lá nhạt (dữ liệu)
};

/* ================= HELPER ================= */

function fill(cell, color) {
    cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: color },
    };
}

function bold(cell) {
    cell.font = { ...cell.font, bold: true };
}

/* ================= MAIN ================= */

export async function exportAttendanceExcel(input, type) {
    const data = input?.data ?? input;
    if (!data) throw new Error("exportAttendanceExcel: invalid input");

    const {
        schoolName,
        teamName,
        exelDate,
        shifts = [],
        attendanceRecordExelResponse = [],
    } = data;

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet(teamName || "Điểm danh");

    /* ================= HEADER TEXT ================= */

    sheet.mergeCells("A1", "Z1");
    const titleCell = sheet.getCell("A1");
    titleCell.value = "BẢNG THỐNG KÊ ĐIỂM DANH NGÀY";
    titleCell.font = { bold: true, size: 16, color: { argb: COLORS.RED_TEXT } };
    titleCell.alignment = { horizontal: "left", indent: 1 };

    sheet.mergeCells("A2", "Z2");
    sheet.getCell("A2").value = `Trường: ${schoolName}`;
    bold(sheet.getCell("A2"));
    sheet.getCell("A2").alignment = { horizontal: "left", indent: 1 };

    sheet.mergeCells("A3", "Z3");
    sheet.getCell("A3").value =
        type === "TEACHER" ? `Phòng ban: ${teamName}` : `Lớp: ${teamName}`;
    bold(sheet.getCell("A3"));
    sheet.getCell("A3").alignment = { horizontal: "left", indent: 1 };

    sheet.mergeCells("A4", "Z4");
    sheet.getCell("A4").value = `Ngày: ${exelDate.split("-").reverse().join("/")}`;
    bold(sheet.getCell("A4"));
    sheet.getCell("A4").alignment = { horizontal: "left", indent: 1 };

    /* ================= TABLE HEADER ================= */

    const startRow = 6;

    // STT + Họ tên
    sheet.mergeCells(startRow, 1, startRow + 2, 1);
    sheet.mergeCells(startRow, 2, startRow + 2, 2);
    sheet.getCell(startRow, 1).value = "STT";
    sheet.getCell(startRow, 2).value = "Họ và tên";
    bold(sheet.getCell(startRow, 1));
    bold(sheet.getCell(startRow, 2));
    fill(sheet.getCell(startRow, 1), COLORS.YELLOW);
    fill(sheet.getCell(startRow, 2), COLORS.YELLOW);

    let colIndex = 3;

    // BUỔI HỌC
    sheet.mergeCells(
        startRow,
        colIndex,
        startRow,
        colIndex + shifts.length * SHIFT_COL_SPAN - 1
    );
    sheet.getCell(startRow, colIndex).value = "BUỔI HỌC";
    bold(sheet.getCell(startRow, colIndex));
    fill(sheet.getCell(startRow, colIndex), COLORS.BLUE);

    shifts.forEach((shift) => {
        sheet.mergeCells(
            startRow + 1,
            colIndex,
            startRow + 1,
            colIndex + SHIFT_COL_SPAN - 1
        );
        sheet.getCell(startRow + 1, colIndex).value =
            `${shift.name.trim()} (${shift.startTime} - ${shift.endTime})`;
        bold(sheet.getCell(startRow + 1, colIndex));
        fill(sheet.getCell(startRow + 1, colIndex), COLORS.YELLOW);

        SHIFT_COLUMNS.forEach((title, i) => {
            const cell = sheet.getCell(startRow + 2, colIndex + i);
            cell.value = title;
            bold(cell);
            fill(cell, COLORS.YELLOW);
        });

        colIndex += SHIFT_COL_SPAN;
    });

    // ĐIỂM DANH ĂN
    const eatStartCol = colIndex;

    sheet.mergeCells(
        startRow,
        colIndex,
        startRow,
        colIndex + EAT_COL_SPAN - 1
    );
    sheet.getCell(startRow, colIndex).value = "ĐIỂM DANH ĂN";
    bold(sheet.getCell(startRow, colIndex));
    fill(sheet.getCell(startRow, colIndex), COLORS.EAT_HEADER);

    sheet.mergeCells(
        startRow + 1,
        colIndex,
        startRow + 1,
        colIndex + EAT_COL_SPAN - 1
    );
    sheet.getCell(startRow + 1, colIndex).value = "Bữa ăn trong ngày";
    bold(sheet.getCell(startRow + 1, colIndex));
    fill(sheet.getCell(startRow + 1, colIndex), COLORS.EAT_SUB_HEADER);

    EAT_COLUMNS.forEach((title, i) => {
        const cell = sheet.getCell(startRow + 2, colIndex + i);
        cell.value = title;
        bold(cell);
        fill(cell, COLORS.EAT_SUB_HEADER);
    });

    colIndex += EAT_COL_SPAN;

    /* ================= DATA ================= */

    let rowIndex = startRow + 3;

    const summaryByShift = {};
    shifts.forEach((s) => {
        summaryByShift[s.id] = {
            present: 0,
            excused: 0,
            unexcused: 0,
            late: 0,
            early: 0,
            lateOut: 0,
        };
    });

    const eatSummary = {
        breakfast: 0,
        secondBreakfast: 0,
        lunch: 0,
        afternoon: 0,
        secondAfternoon: 0,
        dinner: 0,
    };

    attendanceRecordExelResponse.forEach((student, index) => {
        sheet.getCell(rowIndex, 1).value = index + 1;
        sheet.getCell(rowIndex, 2).value = student.targetName;
        fill(sheet.getCell(rowIndex, 1), COLORS.LIGHT_GRAY);
        fill(sheet.getCell(rowIndex, 2), COLORS.LIGHT_GRAY);

        let c = 3;
        shifts.forEach((shift) => {
            const record = student.attendanceRecordList?.find(
                (r) => Number(r.shiftId) === Number(shift.id)
            );

            for (let i = 0; i < SHIFT_COL_SPAN; i++) {
                fill(sheet.getCell(rowIndex, c + i), COLORS.GREEN);
            }

            if (record) {
                if (record.status === "PRESENT") sheet.getCell(rowIndex, c).value = "✓";
                if (record.status === "EXCUSED_ABSENT")
                    sheet.getCell(rowIndex, c + 1).value = "✓";
                if (record.status === "UNEXCUSED_ABSENT")
                    sheet.getCell(rowIndex, c + 2).value = "✓";

                sheet.getCell(rowIndex, c + 3).value = record.checkinTime || "";
                sheet.getCell(rowIndex, c + 4).value = record.checkoutTime || "";
                if (record.lateCheckinMinutes > 0)
                    sheet.getCell(rowIndex, c + 5).value = record.lateCheckinMinutes;
                if (record.earlyCheckoutMinutes > 0)
                    sheet.getCell(rowIndex, c + 6).value = record.earlyCheckoutMinutes;
                if (record.lateCheckoutMinutes > 0)
                    sheet.getCell(rowIndex, c + 7).value = record.lateCheckoutMinutes;

                const sum = summaryByShift[shift.id];
                if (record.status === "PRESENT") sum.present++;
                if (record.status === "EXCUSED_ABSENT") sum.excused++;
                if (record.status === "UNEXCUSED_ABSENT") sum.unexcused++;
                if (record.lateCheckinMinutes > 0) sum.late++;
                if (record.earlyCheckoutMinutes > 0) sum.early++;
                if (record.lateCheckoutMinutes > 0) sum.lateOut++;
            }
            c += SHIFT_COL_SPAN;
        });

        const eat = student.attendanceEat || {};
        if (eat.breakfast) eatSummary.breakfast++;
        if (eat.secondBreakfast) eatSummary.secondBreakfast++;
        if (eat.lunch) eatSummary.lunch++;
        if (eat.afternoon) eatSummary.afternoon++;
        if (eat.secondAfternoon) eatSummary.secondAfternoon++;
        if (eat.dinner) eatSummary.dinner++;

        [
            eat.breakfast,
            eat.secondBreakfast,
            eat.lunch,
            eat.afternoon,
            eat.secondAfternoon,
            eat.dinner,
        ].forEach((v, i) => {
            sheet.getCell(rowIndex, eatStartCol + i).value = v ? "✓" : "";
            fill(sheet.getCell(rowIndex, eatStartCol + i), COLORS.EAT_CELL);
        });

        rowIndex++;
    });

    /* ================= TOTAL ================= */

    sheet.mergeCells(rowIndex, 1, rowIndex, 2);
    const totalCell = sheet.getCell(rowIndex, 1);
    totalCell.value = "TỔNG";
    bold(totalCell);
    totalCell.alignment = { horizontal: "center", vertical: "middle" };
    fill(totalCell, COLORS.YELLOW);
    fill(sheet.getCell(rowIndex, 2), COLORS.YELLOW);

    let c = 3;
    shifts.forEach((shift) => {
        const sum = summaryByShift[shift.id];
        for (let i = 0; i < SHIFT_COL_SPAN; i++) {
            fill(sheet.getCell(rowIndex, c + i), COLORS.BLUE);
            bold(sheet.getCell(rowIndex, c + i));
        }
        sheet.getCell(rowIndex, c).value = sum.present;
        sheet.getCell(rowIndex, c + 1).value = sum.excused;
        sheet.getCell(rowIndex, c + 2).value = sum.unexcused;
        sheet.getCell(rowIndex, c + 5).value = sum.late;
        sheet.getCell(rowIndex, c + 6).value = sum.early;
        sheet.getCell(rowIndex, c + 7).value = sum.lateOut;
        c += SHIFT_COL_SPAN;
    });

    [
        eatSummary.breakfast,
        eatSummary.secondBreakfast,
        eatSummary.lunch,
        eatSummary.afternoon,
        eatSummary.secondAfternoon,
        eatSummary.dinner,
    ].forEach((v, i) => {
        const cell = sheet.getCell(rowIndex, eatStartCol + i);
        cell.value = v;

        // CHỈ KHU ĂN – DÒNG TỔNG
        fill(cell, COLORS.EAT_HEADER);
        bold(cell);
    });

    /* ================= BORDER & EXPORT ================= */

    const tableEndCol =
        2 + shifts.length * SHIFT_COL_SPAN + EAT_COL_SPAN;

    for (let r = startRow; r <= rowIndex; r++) {
        for (let c = 1; c <= tableEndCol; c++) {
            sheet.getCell(r, c).border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" },
            };
            sheet.getCell(r, c).alignment = {
                vertical: "middle",
                horizontal: "center",
                wrapText: true,
            };
        }
    }
    sheet.columns.forEach((col, index) => {
        if (index === 0) {
            col.width = 6;      // STT
        } else if (index === 1) {
            col.width = 32;     // HỌ VÀ TÊN (to riêng)
        } else {
            col.width = 14;     // các cột còn lại
        }
    });
    const dataStartRow = startRow + 3;

    for (let r = dataStartRow; r < rowIndex; r++) {
        sheet.getCell(r, 2).alignment = {
            vertical: "middle",
            horizontal: "left",
            wrapText: true,
            indent: 1,
        };
    }
    sheet.pageSetup.orientation = "landscape";
    sheet.pageSetup.paperSize = 9; // A4

    sheet.pageSetup.fitToPage = true;
    sheet.pageSetup.fitToWidth = 1;
    sheet.pageSetup.fitToHeight = 0;

    sheet.pageSetup.printTitlesRow = `${startRow}:${startRow + 2}`;

    sheet.views = [
        {
            state: "frozen",
            ySplit: startRow + 2,
        },
    ];

    const buffer = await workbook.xlsx.writeBuffer();
    let myDate = moment().format("DD-MM-YYYY hh:mm:ss");
    saveAs(
        new Blob([buffer]),
        `${type === "TEACHER" ? "CHAM_CONG" : "DIEM_DANH"}_${teamName}_${exelDate}_${myDate}.xlsx`
    );
}
